import { useState } from 'react'
import { Zap, Calendar, Send, CheckCircle, AlertCircle, Eye, Share2, Heart, MessageCircle } from 'lucide-react'

export default function AIAutoSocialMediaPage() {
  const [connectedAccounts, setConnectedAccounts] = useState([
    { id: 1, platform: 'Facebook', handle: 'CybertectionGuardBot', followers: '45.2K', status: 'Connected', color: 'from-blue-600' },
    { id: 2, platform: 'Twitter/X', handle: '@CybertectionGB', followers: '32.8K', status: 'Connected', color: 'from-slate-600' },
    { id: 3, platform: 'Instagram', handle: 'cybertection_guardbot', followers: '67.5K', status: 'Connected', color: 'from-pink-600' },
    { id: 4, platform: 'LinkedIn', handle: 'Cybertection Inc', followers: '12.3K', status: 'Connected', color: 'from-blue-700' },
  ])

  const [scheduledPosts, setScheduledPosts] = useState([
    { id: 1, content: 'New security update available! Protect your system with the latest Cybertection features.', platforms: ['Facebook', 'Twitter', 'LinkedIn'], scheduleTime: '2024-02-20 10:00 AM', status: 'Scheduled' },
    { id: 2, content: 'Did you know? 78% of cyber attacks target small businesses. Stay protected with Cybertection.', platforms: ['Instagram', 'Twitter'], scheduleTime: '2024-02-21 2:00 PM', status: 'Scheduled' },
    { id: 3, content: 'Customer spotlight: Learn how TechCorp reduced security incidents by 95% with our platform.', platforms: ['LinkedIn', 'Facebook'], scheduleTime: '2024-02-22 9:00 AM', status: 'Scheduled' },
  ])

  const [postHistory, setPostHistory] = useState([
    { id: 1, content: 'Launch announcement: Cybertection AI is now live!', platforms: ['All'], postedTime: '2024-02-18 9:30 AM', engagement: { likes: 245, shares: 18, comments: 42 } },
    { id: 2, content: 'Security tip: Always enable two-factor authentication', platforms: ['All'], postedTime: '2024-02-17 1:00 PM', engagement: { likes: 189, shares: 12, comments: 28 } },
    { id: 3, content: 'Q&A session next week with our security experts!', platforms: ['Facebook', 'Instagram'], postedTime: '2024-02-16 3:45 PM', engagement: { likes: 312, shares: 45, comments: 67 } },
  ])

  const [newPostContent, setNewPostContent] = useState('')
  const [selectedPlatforms, setSelectedPlatforms] = useState(['Facebook'])
  const [isGenerating, setIsGenerating] = useState(false)
  const [postGenerated, setPostGenerated] = useState(null)

  const generatePostWithAI = () => {
    setIsGenerating(true)
    setTimeout(() => {
      const suggestions = [
        "Cybertection's latest update brings military-grade encryption to your devices. Your security, amplified. 🛡️ #Cybersecurity",
        "Worried about data breaches? Cybertection monitors 24/7 so you can focus on growing your business. #InfoSec",
        "Did you know? Cybertection blocked 15M+ threats last month. Experience the difference enterprise-grade security makes. 🔒",
        "Your data is gold. Our job is to protect it. Cybertection - Enterprise Security Made Simple. #CyberDefense",
      ]
      setPostGenerated(suggestions[Math.floor(Math.random() * suggestions.length)])
      setIsGenerating(false)
    }, 2000)
  }

  const schedulePost = () => {
    if (newPostContent.trim()) {
      alert('Post scheduled successfully! It will be posted to: ' + selectedPlatforms.join(', '))
      setNewPostContent('')
      setPostGenerated(null)
    }
  }

  const totalEngagement = postHistory.reduce((sum, post) => sum + post.engagement.likes + post.engagement.shares + post.engagement.comments, 0)
  const totalReach = connectedAccounts.reduce((sum, acc) => sum + parseInt(acc.followers), 0)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">AI Auto Social Media Poster</h1>
        <p className="text-blue-200">Create, schedule, and manage social media posts across all platforms automatically</p>
      </div>

      {/* Connected Accounts Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Connected Accounts</p>
          <p className="text-3xl font-bold text-blue-400">{connectedAccounts.length}</p>
          <p className="text-xs text-blue-300 mt-2">All active and synced</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Total Followers</p>
          <p className="text-3xl font-bold text-purple-400">{(totalReach / 1000).toFixed(1)}K</p>
          <p className="text-xs text-purple-300 mt-2">Across all platforms</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Total Engagement</p>
          <p className="text-3xl font-bold text-green-400">{totalEngagement.toLocaleString()}</p>
          <p className="text-xs text-green-300 mt-2">Likes, shares, comments</p>
        </div>

        <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
          <p className="text-orange-300/70 text-sm mb-1">Scheduled Posts</p>
          <p className="text-3xl font-bold text-orange-400">{scheduledPosts.length}</p>
          <p className="text-xs text-orange-300 mt-2">Pending publication</p>
        </div>
      </div>

      {/* Connected Social Media Accounts */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Share2 size={24} />
          <span>Connected Social Media Accounts</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {connectedAccounts.map((account) => (
            <div key={account.id} className={`bg-gradient-to-br ${account.color}/20 rounded-lg border border-blue-500/20 p-4`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-white text-lg">{account.platform}</p>
                  <p className="text-sm text-blue-300">@{account.handle}</p>
                </div>
                <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full">
                  {account.status}
                </span>
              </div>
              <div className="pt-3 border-t border-blue-500/20">
                <p className="text-sm text-blue-300">
                  <span className="font-semibold text-white">{account.followers}</span> followers
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create New Post */}
      <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-lg border border-purple-500/30 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">Create & Schedule Post</h2>

        <div className="space-y-4">
          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Post Content</label>
            <textarea
              value={newPostContent || postGenerated || ''}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Write your post here or use AI to generate content..."
              rows="4"
              className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
            />
            <p className="text-xs text-blue-300 mt-2">{newPostContent.length || (postGenerated ? postGenerated.length : 0)}/280 characters</p>
          </div>

          <div>
            <label className="text-blue-300 font-semibold text-sm mb-2 block">Select Platforms</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {connectedAccounts.map((account) => (
                <label key={account.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(account.platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlatforms([...selectedPlatforms, account.platform])
                      } else {
                        setSelectedPlatforms(selectedPlatforms.filter(p => p !== account.platform))
                      }
                    }}
                    className="w-4 h-4 rounded border-blue-500/30"
                  />
                  <span className="text-sm text-blue-300">{account.platform}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={generatePostWithAI}
              disabled={isGenerating}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Zap size={18} />
              <span>{isGenerating ? 'Generating...' : 'AI Generate Post'}</span>
            </button>

            <button
              onClick={schedulePost}
              disabled={!newPostContent && !postGenerated || selectedPlatforms.length === 0}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Send size={18} />
              <span>Post Now</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scheduled Posts */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Calendar size={24} />
          <span>Scheduled Posts</span>
        </h2>

        <div className="space-y-3">
          {scheduledPosts.map((post) => (
            <div key={post.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-white font-semibold">{post.content}</p>
                  <div className="flex items-center space-x-2 mt-2 text-sm text-blue-300">
                    <Calendar size={16} />
                    <span>{post.scheduleTime}</span>
                  </div>
                </div>
                <span className="px-3 py-1 bg-yellow-500/30 text-yellow-300 text-xs font-bold rounded-full whitespace-nowrap ml-4">
                  {post.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 pt-3 border-t border-blue-500/20">
                {post.platforms.map((platform) => (
                  <span key={platform} className="inline-block bg-blue-500/20 text-blue-300 text-xs font-semibold px-2 py-1 rounded">
                    {platform}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Post History & Analytics */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Eye size={24} />
          <span>Post History & Engagement</span>
        </h2>

        <div className="space-y-3">
          {postHistory.map((post) => (
            <div key={post.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="text-white font-semibold">{post.content}</p>
                  <div className="flex items-center space-x-2 mt-2 text-sm text-blue-300">
                    <Calendar size={16} />
                    <span>{post.postedTime}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 pt-3 border-t border-blue-500/20">
                <div className="flex items-center space-x-2 text-red-400">
                  <Heart size={18} />
                  <span className="font-semibold">{post.engagement.likes}</span>
                </div>
                <div className="flex items-center space-x-2 text-green-400">
                  <Share2 size={18} />
                  <span className="font-semibold">{post.engagement.shares}</span>
                </div>
                <div className="flex items-center space-x-2 text-blue-400">
                  <MessageCircle size={18} />
                  <span className="font-semibold">{post.engagement.comments}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/30 p-6">
        <h3 className="text-lg font-bold text-yellow-300 mb-2 flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>Important Disclaimer</span>
        </h3>
        <p className="text-yellow-200 text-sm">
          All posts made through this system will clearly indicate they are AI-assisted. You maintain full control and approval over all content before posting. Ensure all content complies with platform policies and applicable laws. The system monitors posting and engagement automatically.
        </p>
      </div>
    </div>
  )
}
